import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { List as LoadingState } from 'react-content-loader';
import { useHistory, useLocation } from 'react-router-dom';
import Input from 'components/inputs/Input';
import Button from 'components/buttons/Button';
import SectionWrapper from 'pages/Layout/SectionWrapper';
import {
  Filter,
  StyleTable,
  HeaderSection,
  ButtonSection,
  FilterSelectWrapper
} from '../Styled';
import { selectUser } from 'redux/slices/userSlice';
import TableFooter from 'components/TableFooter';
import { SuccessAlert } from 'components/Alert';
import AgentTable from './AgentTable';
import { getAgents } from 'services/rest/SuperAdmin/Agents/getAgents';

const Agent = () => {
  const { t } = useTranslation(['super_admin_agent']);
  const history = useHistory();
  const { currentUser } = useSelector(selectUser);
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState<any | null>(null);

  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('id-asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const paginate = (pageNumber: any) => setCurrentPage(pageNumber);

  const location = useLocation();
  const { state } = location as any;
  const [backendSuccess, setBackendSuccess] = useState<any | null>(null);

  const showAlertSuccessCompanyCreated = async () => {
    if (typeof state === 'string') {
      setBackendSuccess(state);
    }
  };

  const getData = async () => {
    const { data } = await getAgents(currentUser?.token, 500, 1);
    console.log('Data', data);

    setAgents(data);
    showAlertSuccessCompanyCreated();
    setLoading(false);
  };

  useEffect(() => {
    getData();
  }, []);

  const filteredAgents = agents?.filter((data: any) => {
    const agent = data as any;
    console.log('agent', agent);

    const filter = filterText.toLowerCase();
    switch (statusFilter) {
      case 'id':
        return agent.id?.toString().includes(filter);
      case 'name':
        return agent.name?.toLowerCase().includes(filter);
      case 'lastName':
        return agent.lastName?.toLowerCase().includes(filter);
      case 'email':
        return agent.email?.toLowerCase().includes(filter);
      default:
        return (
          agent.id?.toString().includes(filter) ||
          agent.name?.toLowerCase().includes(filter) ||
          agent.lastName?.toLowerCase().includes(filter) ||
          agent.email?.toLowerCase().includes(filter)
        );
    }
  });

  const currentAgents = filterText
    ? filteredAgents
    : agents?.slice(indexOfFirstPost, indexOfLastPost);

  const getFilterColor = (filter: string) => {
    switch (filter) {
      case 'id':
        return '#1a4dcc'; // azul
      case 'name':
        return '#1a4dcc';
      case 'username':
        return '#1a4dcc';
      case 'company':
        return '#1a4dcc';
      default:
        return '#222';
    }
  };

  const getSortedAgents = (agents: any[]) => {
    if (!agents) return [];
    const sorted = [...agents];
    switch (orderBy) {
      case 'id-asc':
        sorted.sort((a, b) => a.id - b.id);
        break;
      case 'id-desc':
        sorted.sort((a, b) => b.id - a.id);
        break;
      case 'name-asc':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'email-asc':
        sorted.sort((a, b) => a.email.localeCompare(b.username));
        break;
      case 'email-desc':
        sorted.sort((a, b) => b.email.localeCompare(a.username));
        break;
      default:
        break;
    }
    return sorted;
  };

  const sortedAgents = getSortedAgents(currentAgents);

  if (loading) {
    return <LoadingState height="230px" width="100%" />;
  }

  return (
    <>
      {backendSuccess && (
        <SuccessAlert
          text={backendSuccess}
          allowClose={true}
          onClose={() => setBackendSuccess(null)}
        />
      )}

      <Suspense fallback={<LoadingState height="230px" width="100%" />}>
        <SectionWrapper>
          <StyleTable>
            <HeaderSection>
              <Filter>
                <Input
                  type="search"
                  placeholder={t('super_admin_agent:search')}
                  value={filterText}
                  onChange={e => {
                    setFilterText(e.target.value);
                  }}
                />
                <FilterSelectWrapper>
                  <label>Filtrar por</label>
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value as any)}
                    style={{
                      color: getFilterColor(statusFilter),
                      fontWeight: 'bold'
                    }}
                  >
                    <option value="all">TODAS</option>
                    <option value="id">ID</option>
                    <option value="name">NOMBRE</option>
                    <option value="email">CORREO</option>
                  </select>
                </FilterSelectWrapper>

                <FilterSelectWrapper focusColor="#1a4dcc">
                  <label>Ordenar Por</label>
                  <select
                    value={orderBy}
                    onChange={e => setOrderBy(e.target.value)}
                  >
                    <option value="id-asc">ID (menor a mayor)</option>
                    <option value="id-desc">ID (mayor a menor)</option>
                    <option value="name-asc">Nombre (A-Z)</option>
                    <option value="name-desc">Nombre (Z-A)</option>
                    <option value="email-asc">Email (A-Z)</option>
                    <option value="email-desc">Email (Z-A)</option>
                  </select>
                </FilterSelectWrapper>
              </Filter>
              <ButtonSection>
                <Button
                  value={t('super_admin_agent:create_agent')}
                  variant="primary"
                  onClick={() => history.replace({ pathname: `/agents/new` })}
                />
              </ButtonSection>
            </HeaderSection>
            <AgentTable
              agents={sortedAgents}
              statusFilter={statusFilter}
              getFilterColor={getFilterColor}
            />
            <TableFooter
              postsPerPage={postsPerPage}
              totalPosts={filterText ? currentAgents : agents?.length}
              paginate={paginate}
              currentPage={currentPage}
            />
          </StyleTable>
        </SectionWrapper>
      </Suspense>
    </>
  );
};

export default Agent;
